import React, { useContext, useState } from "react";
import {
  Box,
  CircularProgress,
  colors,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import ClearIcon from "@material-ui/icons/Clear";
import * as Yup from "yup";

import ExpandedContent from "../../../components/ExpandedContent";
import BalanceDetails from "../../../components/BalanceDetails";
import { PageTitle } from "../../../../../components/page-title/PageTitle";
import Business, { BusinessScopes } from "../../../../../models/Business";
import { AmountDetail } from "../../../components/AmountDetail";
import {
  DeclineButton,
  DefaultButton,
  OutlineButton,
} from "../../../components/Buttons";
import ModalV2 from "../../../components/ModalV2";
import AvatarHeader from "../../../components/AvatarHeader";
import { AppScrollbar } from "../../../../../components/scrollbar/AppScrollbar";
import { InputField } from "../../../../../components/input-field/InputField";
import { FormikHelpers, useFormik } from "formik";
import ICIPAdminService from "../../../../../services/ICIPAdminService";
import BusinessService from "../../../../../services/BusinessService";
import { NotificationDispatchContext } from "../../../../../context/NotificationContext";

interface WalletOverviewProps {
  business: Business;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    subMain: {
      padding: "32px 32px",
      display: "flex",
      alignItems: "center",
    },
    actions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
    },
    actionItem: {
      marginLeft: 20,
    },
    discountWrapper: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  }),
  { name: "personal-wallet-overview-tab-styles" }
);

export default function WalletOverview(props: WalletOverviewProps) {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [business, setBusiness] = useState(props.business);
  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  // Context
  const dispatch = useContext(NotificationDispatchContext);

  const discountCallback = (business: Business) => {
    setOpenDiscountModal(false);
    setBusiness(business);
    dispatch({
      data: {
        type: "success",
        message: intl.formatMessage({
          id: "sharedScreen.editingDiscountSuccessfully",
        }),
      },
    });
  };

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.businessWallet",
        })}`}
      />
      <Box component="div" className={classes.root}>
        <Box component="div">
          <Box component="div" className={classes.subMain}>
            <Box component="div" style={{ flexGrow: 1 }}>
              <Typography variant="h4" component="h4">
                <FormattedMessage id="sharedScreen.wallet" />
              </Typography>
            </Box>
            <DeclineButton type="button" disabled>
              <FormattedMessage id="sharedScreen.transferFunds" />
            </DeclineButton>
            <Box ml={2}>
              <DefaultButton
                type="button"
                onClick={(_) => setOpenDiscountModal(true)}
              >
                <FormattedMessage id="sharedScreen.editDiscount" />
              </DefaultButton>
            </Box>
          </Box>
          <ExpandedContent
            title={intl.formatMessage({ id: "sharedScreen.balances" })}
          >
            <BalanceDetails accounts={business.accounts} />
          </ExpandedContent>
          <ExpandedContent
            title={intl.formatMessage({ id: "sharedScreen.discounts" })}
          >
            <Box className={classes.discountWrapper}>
              <Grid container>
                <Grid item xs={12} sm={4}>
                  <AmountDetail
                    title={<FormattedMessage id="wallet.universalWallet" />}
                    subTitle={`${business.universal_discount} %`}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <AmountDetail
                    title={<FormattedMessage id="wallet.creditCommunication" />}
                    subTitle={`${business.communication_discount} %`}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <AmountDetail
                    title={<FormattedMessage id="wallet.fuel" />}
                    subTitle={`${business.fuel_discount} %`}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <AmountDetail
                    title={<FormattedMessage id="wallet.toll" />}
                    subTitle={`${business.toll_discount} %`}
                  />
                </Grid>
              </Grid>
            </Box>
          </ExpandedContent>
        </Box>
      </Box>

      <ModalV2
        open={openDiscountModal}
        onClose={() => setOpenDiscountModal(false)}
      >
        <DiscountForm
          business={business}
          callback={discountCallback}
          closeCallback={() => setOpenDiscountModal(false)}
        />
      </ModalV2>
    </>
  );
}

interface DiscountFormProps {
  business: Business;
  callback: (business: Business) => void;
  closeCallback: () => void;
}

const useDiscountFormStyles = makeStyles(
  (theme: any) => ({
    root: {},
    wrapper: {
      "& label": {
        fontSize: "0.86rem",
        fontWeight: theme.typography.fontWeightMediumm,
      },
      "& h4": {
        fontWeight: theme.typography.fontWeightBold,
      },
      "& input": {
        fontSize: 18,
        padding: "14px 10px 14px 14px",
        fontWeight: theme.typography.fontWeightBold,
      },
    },
    modalHeader: {
      padding: "0 28px 0 32px",
      borderBottom: "1px solid #e8e8e8",
    },
    gridItem: {
      height: "70px",
      display: "flex",
      alignItems: "center",
    },
    actionItem: {
      width: "100%",
    },
    mainTitle: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    avatarWrapper: {
      width: "100%",
    },
    modalContent: {
      height: "calc(100vh - 150px)",
    },
    modalFooter: {
      height: "80px",
      padding: "10px 32px 0 32px",
    },
    footerActionWrapper: {
      display: "flex",
      alignItems: "center",
    },
    footerAction: {
      display: "flex",
      alignItems: "center",
    },
    contentWrapper: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100%",
      width: "100%",
      padding: 0,
      margin: 0,
      backgroundColor: theme.palette.background.paper,
    },
    formItems: {},
    percent: {
      fontSize: 16,
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightBold,
    },
    error: {
      textAlign: "left",
      fontSize: "1rem",
      color: colors.red[700],
    },
  }),
  { name: "discount-form-styles" }
);

function DiscountForm(props: DiscountFormProps) {
  const classes = useDiscountFormStyles(props);
  const intl = useIntl();
  const { business, callback, closeCallback } = props;

  // Events

  const actionState = () => {
    return (
      (formik.values.universal.length > 0 ||
        formik.values.credit.length > 0 ||
        formik.values.fuel.length > 0 ||
        formik.values.toll.length > 0) &&
      !formik.isSubmitting
    );
  };

  // Handle formik

  const initFormValues = () => {
    return {
      universal: business.universal_discount,
      credit: business.communication_discount,
      fuel: business.fuel_discount,
      toll: business.toll_discount,
    };
  };

  const formValidation = () => {
    return {
      universal: Yup.string().required(
        intl.formatMessage({ id: "error.amount" })
      ),
      credit: Yup.string().required(intl.formatMessage({ id: "error.amount" })),
      fuel: Yup.string().required(intl.formatMessage({ id: "error.amount" })),
      toll: Yup.string().required(intl.formatMessage({ id: "error.amount" })),
    };
  };

  const formik = useFormik({
    initialValues: initFormValues(),
    validationSchema: Yup.object().shape({
      ...formValidation(),
    }),
    onSubmit: async (
      values: any,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const response = await ICIPAdminService.editBusinessDDiscount(
          business.id,
          formik.values
        );

        if (response.isSuccess) {
          switch (response.data.code) {
            case 100: // Ok
              let fetchResponse = await BusinessService.getById(
                business.id,
                `${BusinessScopes.docs}|${BusinessScopes.accounts}|${BusinessScopes.owner}|${BusinessScopes.bank}`
              );

              if (fetchResponse.isSuccess && fetchResponse.data.code === 100) {
                let business = Business.fromJson(
                  JSON.parse(JSON.stringify(fetchResponse.data.data))
                );

                callback(business);
              } else {
                setErrors({
                  unknown: intl.formatMessage({ id: "error.unknownError" }),
                });
              }
              setSubmitting(false);
              break;
            case 150:
              // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              setSubmitting(false);
              break;
            default:
              setErrors({ error: response.data.message });
              setSubmitting(false);
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
          setSubmitting(false);
        }
      } catch (error) {
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
        setSubmitting(false);
      }
    },
  });

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const discountChange = (event: any, key: string) => {
    let amount: string = event.currentTarget.value;

    if (amount.length !== 0 && !Number(amount)) return;
    formik.setFieldValue(key, amount);
  };

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors.universal) {
      return formik.errors.universal as string;
    }

    if (formik.errors.credit) {
      return formik.errors.credit as string;
    }

    if (formik.errors.fuel) {
      return formik.errors.fuel as string;
    }

    if (formik.errors.toll) {
      return formik.errors.toll as string;
    }

    if (formik.errors.business_id) {
      return formik.errors.business_id as string;
    }

    if (formik.errors.error) {
      return formik.errors.error as string;
    }

    return "";
  };

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.editDiscount",
        })}`}
      />
      <Box className={classes.root}>
        <Box className={classes.modalHeader}>
          <Grid container>
            <Grid item sm={4}>
              <Box className={classes.gridItem}>
                <Box className={classes.avatarWrapper}>
                  <AvatarHeader
                    size={40}
                    avatar={business.avatar()}
                    validationStatus={business.status()}
                    accountStatus={business.status()}
                    subTitle={business.address()}
                    title={business.fullName()}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item sm={4}>
              <Box className={classes.gridItem}>
                <Box className={classes.mainTitle}>
                  <Typography component="h4" variant="h4">
                    <FormattedMessage id="sharedScreen.editDiscount" />
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item sm={4}>
              <Box className={classes.gridItem}>
                <Box
                  className={classes.actionItem}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <IconButton onClick={(_) => closeCallback()}>
                    <ClearIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box component="div">
          <AppScrollbar>
            <Box className={classes.modalContent}>
              <Box
                component="div"
                style={{ paddingBottom: "2rem" }}
                className={classes.contentWrapper}
              >
                <Grid container>
                  <Grid item sm={4} />
                  <Grid item sm={4}>
                    <Box component="div" className={classes.wrapper}>
                      <Box component="div" className={classes.formItems}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box component="div">
                              <InputField
                                fullWidth
                                id="universal"
                                name="universal"
                                label={intl.formatMessage({
                                  id: "wallet.universalWallet",
                                })}
                                placeholder={""}
                                type="text"
                                endAdornment={
                                  <Box
                                    component="span"
                                    className={classes.percent}
                                  >
                                    %
                                  </Box>
                                }
                                error={Boolean(formik.errors.universal)}
                                disabled={formik.isSubmitting}
                                value={formik.values.universal}
                                onChange={(event) =>
                                  discountChange(event, "universal")
                                }
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box component="div">
                              <InputField
                                fullWidth
                                id="credit"
                                name="credit"
                                label={intl.formatMessage({
                                  id: "wallet.creditCommunication",
                                })}
                                placeholder={""}
                                type="text"
                                endAdornment={
                                  <Box
                                    component="span"
                                    className={classes.percent}
                                  >
                                    %
                                  </Box>
                                }
                                error={Boolean(formik.errors.credit)}
                                disabled={formik.isSubmitting}
                                value={formik.values.credit}
                                onChange={(event) =>
                                  discountChange(event, "credit")
                                }
                              />
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                          <Grid item xs={6}>
                            <Box component="div">
                              <InputField
                                fullWidth
                                id="fuel"
                                name="fuel"
                                label={intl.formatMessage({
                                  id: "wallet.fuel",
                                })}
                                placeholder={""}
                                type="text"
                                endAdornment={
                                  <Box
                                    component="span"
                                    className={classes.percent}
                                  >
                                    %
                                  </Box>
                                }
                                error={Boolean(formik.errors.fuel)}
                                disabled={formik.isSubmitting}
                                value={formik.values.fuel}
                                onChange={(event) =>
                                  discountChange(event, "fuel")
                                }
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box component="div">
                              <InputField
                                fullWidth
                                id="toll"
                                name="toll"
                                label={intl.formatMessage({
                                  id: "wallet.toll",
                                })}
                                placeholder={""}
                                type="text"
                                endAdornment={
                                  <Box
                                    component="span"
                                    className={classes.percent}
                                  >
                                    %
                                  </Box>
                                }
                                error={Boolean(formik.errors.toll)}
                                disabled={formik.isSubmitting}
                                value={formik.values.toll}
                                onChange={(event) =>
                                  discountChange(event, "toll")
                                }
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      <Box mt={2} component="div" className={classes.error}>
                        {genericError()}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item sm={4} />
                </Grid>
              </Box>
            </Box>
          </AppScrollbar>
        </Box>
        <Box className={classes.modalFooter}>
          <Grid container>
            <Grid item sm={4} />
            <Grid item sm={4}>
              <Box className={classes.footerActionWrapper}>
                <Box component="div" style={{ flexGrow: 1 }}>
                  {formik.isSubmitting ? (
                    <CircularProgress
                      color="secondary"
                      size={20}
                      thickness={4}
                    />
                  ) : (
                    <></>
                  )}
                </Box>
                <Box component="div" className={classes.footerAction}>
                  <Box ml={1}>
                    <OutlineButton
                      type="button"
                      onClick={(_) => closeCallback()}
                      disabled={formik.isSubmitting}
                    >
                      <FormattedMessage id="sharedScreen.cancel" />
                    </OutlineButton>
                  </Box>
                  <Box ml={2}>
                    <DefaultButton
                      type="button"
                      disabled={formik.isSubmitting || !actionState()}
                      onClick={(_) => handleSubmit()}
                    >
                      <FormattedMessage id="sharedScreen.editDiscount" />
                    </DefaultButton>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item sm={4} />
          </Grid>
        </Box>
      </Box>
    </>
  );
}
